/**
 * SecretField — maskovaný řádek s klíčem/heslem + toggle viditelnosti + copy.
 */
import { useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

async function copyText(text: string, ok: string, fail: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(ok);
    return;
  } catch {
    /* fallback */
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
    toast.success(ok);
  } catch {
    toast.error(fail);
  }
  document.body.removeChild(el);
}

export interface SecretFieldProps {
  label: string;
  value: string | null | undefined;
  /** Pokud nastaveno, default mask je tento string místo bullets */
  placeholder?: string;
}

export function SecretField({ label, value, placeholder }: SecretFieldProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const v = value ?? '';
  const empty = !v;
  return (
    <div>
      <label className='text-xs text-gray-500 mb-1 block'>{label}</label>
      <div className='flex items-center gap-1.5 bg-gray-800 rounded-lg px-2 py-1.5'>
        <code
          className={`flex-1 text-xs font-mono truncate ${
            empty ? 'text-gray-600 italic' : 'text-gray-300'
          }`}
        >
          {empty ? (placeholder ?? t('ui.empty')) : show ? v : '••••••••••••••••'}
        </code>
        {!empty && (
          <>
            <button
              type='button'
              onClick={() => setShow((s) => !s)}
              className='text-gray-600 hover:text-gray-400 flex-shrink-0'
              title={show ? t('common.hide') : t('common.show')}
            >
              {show ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
            <button
              type='button'
              onClick={() => copyText(v, t('ui.copied'), t('ui.copyFailed'))}
              className='text-gray-600 hover:text-gray-400 flex-shrink-0'
              title={t('ui.copy')}
            >
              <Copy size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
